/**
 * SMA Flyer Constants — Single Source of Truth
 * ----------------------------------------------
 * These constants mirror the backend's masterclass.flyerRenderer.js exports.
 * The backend is the canonical source; this file is kept in sync to avoid
 * duplicating template/font metadata in the frontend.
 *
 * If you add a template or font pairing, update BOTH:
 *   1. backend/src/modules/masterclass/masterclass.flyerRenderer.js
 *   2. this file
 *
 * Or better: fetch from the API endpoint GET /api/masterclass/admin/flyer-meta
 * (if available) to guarantee single-source-of-truth at runtime.
 */

// Template cycle order (M, Minimal Dark, is the default template)
export const LAYOUT_CYCLE = ['M', 'N', 'B', 'C', 'A', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L']

export const DEFAULT_LAYOUT = 'M'

// Human-readable names for each layout
export const LAYOUT_NAMES = {
  A: 'Editorial Hero',
  B: 'Bright Editorial',
  C: 'Cinematic Card',
  D: 'Magazine Cover',
  E: 'Portrait Frame',
  F: 'Asymmetric Grid',
  G: 'Full Bleed Minimal',
  H: 'Dark Luxury',
  I: 'Orange Accent',
  J: 'Architectural Editorial',
  K: 'Type-Led Poster',
  L: 'Gallery Window',
  M: 'Minimal Dark',
  N: 'Minimal Light',
}

// All valid layout IDs
export const LAYOUT_IDS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N']

// Curated font pairings (8 pairings, A–H)
export const FONT_PAIRINGS = {
  A: 'Geometric Modern',
  B: 'Display Geometric',
  C: 'Contemporary Sans',
  D: 'Editorial Serif',
  E: 'Classic Editorial',
  F: 'Condensed Display',
  G: 'Modern Grotesk',
  H: 'Space Display',
  S: 'SMA Brand',
}

// All valid font pairing IDs
export const FONT_PAIRING_IDS = Object.keys(FONT_PAIRINGS)

// Template → compatible font pairings (curated for design quality)
export const TEMPLATE_FONT_COMPAT = {
  A: ['A', 'B', 'D', 'G'],        // Editorial Hero
  B: ['A', 'B', 'C', 'G', 'H'],   // Bright Editorial
  C: ['D', 'E', 'F', 'G'],        // Cinematic Card
  D: ['D', 'E', 'B'],             // Magazine Cover
  E: ['D', 'E', 'A', 'G'],        // Portrait Frame
  F: ['G', 'H', 'A', 'C'],        // Asymmetric Grid
  G: ['D', 'E', 'F', 'G'],        // Full Bleed Minimal
  H: ['D', 'E', 'F', 'G'],        // Dark Luxury
  I: ['A', 'B', 'C', 'G', 'H'],   // Orange Accent
  J: ['G', 'H', 'A', 'C'],        // Architectural Editorial
  K: ['F', 'A', 'B', 'H'],        // Type-Led Poster
  L: ['D', 'E', 'A', 'G'],        // Gallery Window
  M: ['S'],                       // Minimal Dark
  N: ['S'],                       // Minimal Light
}

// Templates with dark backgrounds (use light/white logo variant)
export const TEMPLATE_IS_DARK = {
  A: false, B: false, C: true, D: false, E: false, F: false,
  G: true, H: true, I: false, J: false, K: true, L: false,
  M: true, N: false,
}

// Canonical fee text used across all templates
export const FEE_TEXT = 'FREE — SMA Students · UGX 150,000 — Non-SMA Students'

/**
 * Cycle to the next layout in the B→C→A→D→E→F→G→H→I→J→K→L sequence.
 */
export function cycleLayout(current) {
  const normalized = LAYOUT_IDS.includes(current) ? current : DEFAULT_LAYOUT
  const idx = LAYOUT_CYCLE.indexOf(normalized)
  return LAYOUT_CYCLE[(idx + 1) % LAYOUT_CYCLE.length]
}

/**
 * Cycle to the next compatible font pairing for a given layout.
 */
export function cycleFontPairing(currentFont, layout) {
  const compat = TEMPLATE_FONT_COMPAT[layout] || ['A']
  const normalized = FONT_PAIRING_IDS.includes(currentFont) ? currentFont : compat[0]
  const idx = compat.indexOf(normalized)
  if (idx === -1) return compat[0]
  return compat[(idx + 1) % compat.length]
}
